import React, { useEffect, useState, useMemo, useCallback } from "react";
import clsx from "clsx";
import { withRouter, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { auth, firestore } from "../../../../firebase/firebase";
import {
  Drawer,
  List,
  CssBaseline,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  ListSubheader,
  Grid,
  Tooltip,
  Badge,
  useMediaQuery,
  Modal,
  Fade,
  Button,
  Backdrop,
  Paper,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../providers/AuthContext";
import _ from "lodash";
import { SubDrawer } from "./SubDrawers/SubDrawer";
import { HRManagementDrawer } from "./SubDrawers/HRManagementDrawer";
import { useHRSettings } from "../../../../providers/HRSettingsProvider";
import AppToolbar from "./AppToolbar";
import BugReportModal from "./BugReportModal";

import HomeIcon from "@material-ui/icons/Home";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import AssessmentIcon from "@material-ui/icons/Assessment";
import PermContactCalendarIcon from "@material-ui/icons/PermContactCalendar";
import ShopIcon from "@material-ui/icons/Shop";
import PeopleIcon from "@material-ui/icons/People";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import AddIcon from "@material-ui/icons/Add";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import SettingsApplicationsIcon from "@material-ui/icons/SettingsApplications";
import ImportantDevicesIcon from "@material-ui/icons/ImportantDevices";
import ContactMailIcon from "@material-ui/icons/ContactMail";
import ListAltIcon from "@material-ui/icons/ListAlt";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import BuildIcon from "@material-ui/icons/Build";
import BugReportIcon from "@material-ui/icons/BugReport";
import GrainIcon from "@material-ui/icons/Grain";
import GavelIcon from "@material-ui/icons/Gavel";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FolderIcon from "@material-ui/icons/Folder";

const DRAWER_WIDTH = 237;
const DRAWER_WIDTH_CLOSED = 66;
const APPBAR_HEIGHT = 64;
const APPBAR_HEIGHT_MOBILE = 56;

// Timesheet types for badge counting
const TIMESHEET_TYPES = [
  { type: "Office", role: "Approve Office Timesheets" },
  { type: "Shop", role: "Approve Shop Timesheets" },
  { type: "Gravel", role: "Approve Gravel Timesheets" },
  { type: "Construction", role: "Approve Construction Timesheets" },
  { type: "Grain", role: "Approve Grain Timesheets" },
  { type: "Snow", role: "Approve Snow Timesheets" },
];

// Badge reads are capped; the Badge `max` prop renders the "+" form once the cap is hit
const BADGE_QUERY_LIMIT = 100;
const APPROVAL_BADGE_QUERY_LIMIT = 1000;

const useStyles = makeStyles((theme) => ({
  mobileTitle: {
    flex: 1,
    marginLeft: theme.spacing(1),
    fontSize: "1rem",
    fontWeight: 500,
  },
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      width: 0,
    },
  },
  drawerPaper: {
    paddingTop: APPBAR_HEIGHT,
    whiteSpace: "nowrap",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      paddingTop: APPBAR_HEIGHT_MOBILE,
      width: "85vw",
      maxWidth: 300,
    },
  },
  drawerOpen: {
    width: DRAWER_WIDTH,
    boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
    [theme.breakpoints.down("sm")]: {
      width: "85vw",
      maxWidth: 300,
    },
  },
  drawerClose: {
    width: 0,
    overflowX: "hidden",
    [theme.breakpoints.up("sm")]: {
      width: DRAWER_WIDTH_CLOSED,
    },
  },
  // Collapsed state styles
  listItemCollapsed: {
    justifyContent: "center",
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  listItemIconCollapsed: {
    minWidth: "auto",
    justifyContent: "center",
  },
  sectionHeaderCollapsed: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 0,
    minHeight: "auto",
    lineHeight: "normal",
    cursor: "pointer",
  },
  hideOnCollapse: {
    opacity: 1,
    transition: "opacity 0.2s ease",
  },
  hiddenOnCollapse: {
    opacity: 0,
    width: 0,
    overflow: "hidden",
  },
  // Sub drawers container
  subDrawersContainer: {
    position: "fixed",
    top: APPBAR_HEIGHT,
    left: DRAWER_WIDTH,
    bottom: 0,
    display: "flex",
    flexDirection: "row",
    zIndex: 999,
    pointerEvents: "none",
    [theme.breakpoints.down("sm")]: {
      top: APPBAR_HEIGHT_MOBILE,
      left: 0,
      right: 0,
      flexDirection: "column",
      zIndex: 1301,
    },
  },
  // Sub drawers
  subDrawerOpen: {
    position: "relative",
    width: 240,
    borderRadius: 0,
    zIndex: 3,
    boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
    pointerEvents: "auto",
    overflowY: "auto",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      top: APPBAR_HEIGHT_MOBILE,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      zIndex: 1301,
      display: "flex",
      flexDirection: "column",
    },
  },
  subDrawerClose: {
    position: "relative",
    width: 0,
    minWidth: 0,
    overflow: "hidden",
    pointerEvents: "none",
    borderRadius: 0,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  subSubDrawerOpen: {
    position: "relative",
    width: 240,
    borderRadius: 0,
    zIndex: 2,
    boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
    pointerEvents: "auto",
    overflowY: "auto",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      top: APPBAR_HEIGHT_MOBILE,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      zIndex: 1302,
      display: "flex",
      flexDirection: "column",
    },
  },
  subSubDrawerClose: {
    position: "relative",
    width: 0,
    minWidth: 0,
    overflow: "hidden",
    pointerEvents: "none",
    borderRadius: 0,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  subSubSubDrawerOpen: {
    position: "relative",
    width: 240,
    borderRadius: 0,
    zIndex: 1,
    boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
    pointerEvents: "auto",
    overflowY: "auto",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      top: APPBAR_HEIGHT_MOBILE,
      left: 0,
      right: 0,
      bottom: 0,
      width: "100%",
      zIndex: 1303,
      display: "flex",
      flexDirection: "column",
    },
  },
  subSubSubDrawerClose: {
    position: "relative",
    width: 0,
    minWidth: 0,
    overflow: "hidden",
    pointerEvents: "none",
    borderRadius: 0,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  container: {
    display: "flex",
    width: "100%",
    paddingTop: APPBAR_HEIGHT,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      paddingTop: APPBAR_HEIGHT_MOBILE,
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    minHeight: `calc(100vh - ${APPBAR_HEIGHT}px)`,
    [theme.breakpoints.down("sm")]: {
      minHeight: `calc(100vh - ${APPBAR_HEIGHT_MOBILE}px)`,
    },
  },
  listItem: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderRadius: 0,
    transition: "background-color 0.15s ease, border-left 0.15s ease",
    borderLeft: "3px solid transparent",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  listItemActive: {
    backgroundColor: theme.palette.action.selected,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  listItemIcon: {
    minWidth: 36,
    color: theme.palette.type === "dark" ? "#FFC107" : "#616161",
  },
  sectionHeader: {
    cursor: "pointer",
    userSelect: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  expand: {
    transform: "rotate(-90deg)",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: "rotate(0deg)",
  },
  drawerScrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
  },
  drawerScrollAreaCollapsed: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "none",
    "-ms-overflow-style": "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  bottomSection: {
    flexShrink: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  // Overlay for click-away
  overlay: {
    position: "fixed",
    top: APPBAR_HEIGHT,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 998,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    transition: "opacity 0.2s ease",
    [theme.breakpoints.down("sm")]: {
      top: APPBAR_HEIGHT_MOBILE,
      zIndex: 1300,
    },
  },
  overlayHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  // Mobile-specific styles
  mobileIconButton: {
    padding: theme.spacing(1),
  },
  mobileDrawerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 48,
  },
  mobileBackButton: {
    marginRight: theme.spacing(1),
  },
  subDrawerMobileHeader: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(1, 1, 1, 0.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      minHeight: 48,
      backgroundColor: theme.palette.background.paper,
    },
  },
  subDrawerMobileTitle: {
    fontWeight: 500,
    flex: 1,
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    outline: 0,
  },
  modalPaper: {
    padding: theme.spacing(3),
    minWidth: 320,
    maxWidth: 500,
    outline: "none",
    borderRadius: theme.shape.borderRadius,
  },
  dividerCollapsed: {
    margin: theme.spacing(1, 1),
  },
}));

const SideDrawer = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    loading,
    settingsLoaded,
    userInfo,
    roles,
    currentUser,
    userSettings,
    updateUserSettings,
  } = useAuth();
  const { types, subtypes } = useHRSettings();

  // Drawer state
  const [alwaysOpen, setAlwaysOpen] = useState(userSettings?.sidebarAlwaysOpen ?? false);
  const [open, setOpen] = useState(alwaysOpen);
  const [openSubDrawer, setOpenSubDrawer] = useState(false);
  const [openSubSubDrawer, setOpenSubSubDrawer] = useState(false);
  const [openSubSubSubDrawer, setOpenSubSubSubDrawer] = useState(false);
  const [subDrawer, setSubDrawer] = useState(null);
  const [subSubDrawer, setSubSubDrawer] = useState(null);
  const [subSubSubDrawer, setSubSubSubDrawer] = useState(null);

  // Section expanded states
  const [expandedSections, setExpandedSections] = useState({
    admin: userSettings?.adminExpanded ?? true,
    general: userSettings?.generalExpanded ?? true,
    shop: userSettings?.shopExpanded ?? true,
  });

  // Badge counts - only fetched for the roles that can see each badge
  const canManageResumes = roles.includes("Manage Resumes");
  const canManageMessages = roles.includes("Manage Message Center");
  const approvableTypesKey = useMemo(
    () =>
      TIMESHEET_TYPES.filter((t) => roles.includes(t.role))
        .map((t) => t.type)
        .join(","),
    [roles]
  );
  const [resumeBadge, setResumeBadge] = useState(0);
  const [unapprovedBadge, setUnapprovedBadge] = useState(0);
  const [unreadBadge, setUnreadBadge] = useState(0);
  const [myTasksBadge, setMyTasksBadge] = useState(0);

  // Modals
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);

  // Check if any subdrawer is open
  const hasOpenSubDrawer = openSubDrawer || openSubSubDrawer || openSubSubSubDrawer;

  // Show overlay when drawer is open (not pinned) or subdrawers are open
  // On mobile, temporary drawer has its own backdrop, so only show overlay for subdrawers
  const showOverlay = smallScreen
    ? hasOpenSubDrawer
    : (open && !alwaysOpen) || hasOpenSubDrawer;

  // The drawer now paints before userSettings resolves; seed the theme from the
  // last value this browser saw for this user so it does not flash the wrong mode.
  const colorModeCacheKey = currentUser?.uid ? `gtColorMode.${currentUser.uid}` : null;
  useEffect(() => {
    if (!colorModeCacheKey) return;
    try {
      const cached = window.localStorage.getItem(colorModeCacheKey);
      if (cached) props.setColorMode(cached);
    } catch (e) { /* storage unavailable */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize color mode from user settings
  useEffect(() => {
    if (!settingsLoaded) return;
    const mode = userSettings?.colorMode ?? "light";
    props.setColorMode(mode);
    if (!colorModeCacheKey) return;
    try {
      window.localStorage.setItem(colorModeCacheKey, mode);
    } catch (e) { /* storage unavailable */ }
  }, [settingsLoaded, userSettings?.colorMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persisted drawer preferences arrive after the first paint; apply them once.
  useEffect(() => {
    if (!settingsLoaded) return;
    const pinned = userSettings?.sidebarAlwaysOpen ?? false;
    setAlwaysOpen(pinned);
    setOpen((prevOpen) => prevOpen || pinned);
    setExpandedSections({
      admin: userSettings?.adminExpanded ?? true,
      general: userSettings?.generalExpanded ?? true,
      shop: userSettings?.shopExpanded ?? true,
    });
  }, [settingsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Badge for resumes
  useEffect(() => {
    if (!canManageResumes) {
      setResumeBadge(0);
      return undefined;
    }

    let cancelled = false;
    const fetchResumeCount = async () => {
      try {
        const snapshot = await firestore
          .collection("resumes")
          .where("viewed", "==", false)
          .limit(BADGE_QUERY_LIMIT)
          .get();
        if (!cancelled) {
          setResumeBadge(snapshot.docs.length);
        }
      } catch (error) {
        console.error("Error fetching resumes:", error);
      }
    };

    fetchResumeCount();
    return () => {
      cancelled = true;
    };
  }, [canManageResumes]);

  // Badge for messages
  useEffect(() => {
    if (!canManageMessages) {
      setUnreadBadge(0);
      return undefined;
    }

    const unsubscribe = firestore
      .collection("messages")
      .where("read", "==", false)
      .where("directionType", "==", "MO")
      .limit(BADGE_QUERY_LIMIT)
      .onSnapshot((snapshot) => setUnreadBadge(snapshot.docs.length));
    return () => unsubscribe();
  }, [canManageMessages]);

  // Badge for my unresolved tasks
  useEffect(() => {
    if (!userInfo?.uuid) {
      setMyTasksBadge(0);
      return;
    }
    const unsubscribe = firestore
      .collection("taskReports")
      .where("assignedUser", "==", userInfo.uuid)
      .where("status", "!=", "Resolved")
      .orderBy("status")
      .limit(BADGE_QUERY_LIMIT)
      .onSnapshot(
        (snapshot) => setMyTasksBadge(snapshot.docs.length),
        (error) => console.error("Error fetching tasks:", error)
      );
    return () => unsubscribe();
  }, [userInfo?.uuid]);

  // Badge for unapproved timesheets
  useEffect(() => {
    if (loading) {
      return undefined;
    }
    if (!approvableTypesKey) {
      setUnapprovedBadge(0);
      return undefined;
    }

    let cancelled = false;
    const fetchUnapprovedCount = async () => {
      try {
        const snapshot = await firestore
          .collection("timesheets")
          .where("approved", "==", false)
          .where("timesheetType", "in", approvableTypesKey.split(","))
          .orderBy("timesheetID", "desc")
          .limit(APPROVAL_BADGE_QUERY_LIMIT)
          .get();
        // timesheetID 0 means unsubmitted; the desc order keeps those out of the capped window
        const count = snapshot.docs.filter((doc) => doc.data().timesheetID !== 0).length;
        if (!cancelled) {
          setUnapprovedBadge(count);
        }
      } catch (error) {
        console.error("Error fetching unapproved timesheets:", error);
      }
    };

    fetchUnapprovedCount();
    return () => {
      cancelled = true;
    };
  }, [loading, approvableTypesKey]);

  // Build HR items from types/subtypes
  const hrItems = useMemo(() => {
    const items = [];

    const accessibilitySubtype = subtypes.find((s) => s.name === "Accessibility");
    if (accessibilitySubtype) {
      items.push({
        title: "Accessibility",
        icon: null,
        link: accessibilitySubtype.override
          ? accessibilitySubtype.overrideValue
          : `/dashboard/hr/${accessibilitySubtype.parentValue}/${accessibilitySubtype.value}`,
        level: 1,
      });
    }

    types.forEach((type) => {
      const typeItems = [];
      const sortedSubtypes = [...subtypes].sort((a, b) => a.order - b.order);

      sortedSubtypes.forEach((subtype) => {
        if (subtype.parentValue !== type.value || subtype.hasParentFolder || subtype.hidden) {
          return;
        }

        if (!subtype.hasSubfolders) {
          typeItems.push({
            title: subtype.name,
            icon: null,
            link: subtype.override
              ? subtype.overrideValue
              : `/dashboard/hr/${type.value}/${subtype.value}`,
            level: 2,
          });
        } else {
          const subItems = sortedSubtypes
            .filter((s) => s.parentFolder === subtype.value && !s.hidden)
            .map((s) => ({
              title: s.name,
              icon: null,
              link: s.override ? s.overrideValue : `/dashboard/hr/${type.value}/${s.value}`,
              level: 3,
            }));

          typeItems.push({
            title: subtype.name,
            icon: null,
            items: subItems,
            level: 2,
          });
        }
      });

      items.push({ title: type.name, icon: null, items: typeItems, level: 1 });
    });

    return items;
  }, [types, subtypes]);

  // Menu configurations
  const GENERAL_ITEMS = useMemo(
    () => [
      { title: "Home", icon: <HomeIcon />, link: "/dashboard", level: 1 },
      { title: "File Storage", icon: <FolderIcon />, link: "/dashboard/storage", roles: "Use File Storage", level: 1 },
      { title: "Reports", icon: <AssessmentIcon />, link: "/dashboard/reporting", roles: "Access Reports Section", level: 1 },
      {
        title: "Employees",
        icon: <PeopleIcon />,
        link: "/dashboard/users",
        roles: "Create|Update|Delete Employees",
        level: 1,
        items: [
          { title: "Add Employee", icon: <AddIcon />, link: "/dashboard/users/new", roles: "Create|Update|Delete Employees" },
          { title: "DIVIDER" },
          { title: "Employee Manager", icon: null, link: "/dashboard/users", roles: "Create|Update|Delete Employees" },
          { title: "Account Roles", icon: null, link: "/dashboard/users/roles" },
          { title: "Employee Tasks", icon: null, roles: "Create|Update|Delete Employee Tasks", level: 2, items: [
            { title: "Manage Tasks", icon: null, link: "/dashboard/users/tasks", roles: "Create|Update|Delete Employee Tasks" },
            { title: "Task Reports", icon: null, link: "/dashboard/users/taskreports", roles: "Create|Update|Delete Employee Tasks" },
          ]},
          { title: "DIVIDER" },
          { title: "Send Mass Email", icon: null, link: "/dashboard/users/memos" },
          { title: "Send Mobile Notification", icon: null, link: "/dashboard/users/notifs" },
        ],
      },
      { title: "Human Resources", icon: <GavelIcon />, link: "/dashboard/hr", level: 1, items: hrItems },
      {
        title: "Equipment",
        icon: <LocalShippingIcon />,
        link: "/dashboard/equipment",
        roles: "Create|Update|Delete Equipment",
        level: 1,
        items: [
          { title: "Add Equipment", icon: <AddIcon />, link: "/dashboard/equipment/new", roles: "Create|Update|Delete Equipment", level: 2 },
          { title: "DIVIDER" },
          { title: "Equipment Manager", icon: null, link: "/dashboard/equipment/home", level: 2 },
          { title: "Permits", icon: null, link: "/dashboard/equipment/permits", roles: "Create|Update|Delete Permits", level: 2 },
          { title: "Defect Settings", icon: null, link: "/dashboard/equipment/defects", roles: "Manage Defect Settings", level: 2 },
          { title: "Fuel & Mileage", icon: null, link: "/dashboard/equipmentIfta", roles: "Create|Update|Delete Ifta", level: 2 },
        ],
      },
      { title: "Work Orders", icon: <BuildIcon />, link: "/dashboard/workorders", roles: "Create|Update|Delete Work Orders", level: 1 },
      { title: "Customers", icon: <PermContactCalendarIcon />, link: "/dashboard/customers", roles: "Create|Update|Delete Customers", level: 1 },
      {
        title: "Forms & More",
        icon: <ListAltIcon />,
        link: "/dashboard/forms",
        roles: "Create|Update|Delete Forms",
        level: 1,
        items: [
          { title: "Checklists", icon: null, link: "/dashboard/checklists" },
          { title: "PDF Forms", icon: null, link: "/dashboard/pdfforms" },
        ],
      },
      {
        title: "Resumes",
        level: 1,
        icon: <Badge badgeContent={resumeBadge} color="error" max={99} overlap="rectangular"><ContactMailIcon /></Badge>,
        link: "/dashboard/resumes",
        roles: "Manage Resumes",
      },
    ],
    [hrItems, resumeBadge]
  );

  const ADMIN_ITEMS = useMemo(
    () => [
      {
        title: "Timesheet Approval",
        level: 1,
        icon: <Badge badgeContent={unapprovedBadge} color={unapprovedBadge > 0 ? "error" : "primary"} max={999} showZero overlap="rectangular"><AssignmentTurnedInIcon /></Badge>,
        link: "/dashboard/approval/timesheets",
      },
      { title: "Schedules", icon: <CalendarTodayIcon />, link: "/dashboard/Scheduler/view", level: 1 },
      { title: "Bug Reports", icon: <BugReportIcon />, link: "/dashboard/tech/bugreports", level: 1 },
      {
        title: "Settings",
        icon: <SettingsApplicationsIcon />,
        link: "/dashboard/store/settings",
        level: 1,
        items: [
          { title: "Business Settings", icon: null, link: "/dashboard/store/businessSettings", roles: "Edit Business Settings", level: 2 },
          { title: "Shop Settings", icon: null, link: "/dashboard/store/shopSettings", roles: "Edit Shop Settings", level: 2 },
          { title: "Service Settings", icon: null, link: "/dashboard/store/serviceSettings", roles: "Edit Service Settings", level: 2 },
          { title: "Career Settings", icon: null, link: "/dashboard/store/careerSettings", roles: "Edit Career Settings", level: 2 },
          { title: "Zone Settings", icon: null, link: "/dashboard/store/zoneSettings", roles: "Edit Zone Settings", level: 2 },
          { title: "DIVIDER" },
          { title: "Accessibility Settings", icon: null, link: "/dashboard/hr/accessibility/settings", roles: "Edit Accessibility Settings", level: 2 },
          { title: "HR Folder Settings", icon: null, link: "/dashboard/hr/settings", roles: "Edit HR Folder Settings", level: 2 },
          { title: "DIVIDER" },
          { title: "App Settings", icon: null, link: "/dashboard/settings/appSettings", roles: "Update App Message", level: 2 },
        ],
      },
    ],
    [unapprovedBadge]
  );

  const SHOP_ITEMS = useMemo(
    () => [
      {
        title: "Online Orders",
        icon: <ShopIcon />,
        link: "/dashboard/store/orders",
        roles: "Create|Update|Delete Orders",
        level: 1,
        items: [
          { title: "Create New Order", icon: <AddIcon />, link: "/dashboard/store/orders/newSquare", level: 2 },
          { title: "DIVIDER" },
          { title: "Order Reports", icon: null, link: "/dashboard/store/orders", level: 2 },
          { title: "Order Quotes", icon: null, link: "/dashboard/quotes", level: 2 },
        ],
      },
      {
        title: "Products",
        icon: <LocalAtmIcon />,
        link: "/dashboard/store/products",
        roles: "Create|Update|Delete Products",
        level: 1,
        items: [
          { title: "Product Manager", icon: null, link: "/dashboard/products", level: 2 },
          { title: "Category Manager", icon: null, link: "/dashboard/products/categories", level: 2 },
          { title: "Coupon Manager", icon: null, link: "/dashboard/coupons", level: 2 },
          { title: "Gift Card Manager", icon: null, link: "/dashboard/giftCards", level: 2 },
          { title: "Used Equipment", icon: null, link: "/dashboard/store/equipment", level: 2 },
        ],
      },
      {
        title: "Message Center",
        level: 1,
        icon: <Badge badgeContent={unreadBadge} color="primary" max={99} overlap="rectangular"><PhoneAndroidIcon /></Badge>,
        link: "/dashboard/store/conversations",
        roles: "Manage Message Center",
      },
      { title: "Admin Tickets", icon: <GrainIcon />, roles: "Create|Update|Delete Grain Reports", link: "/dashboard/grains", level: 1 },
      {
        title: "Tickets/Invoices",
        icon: <MenuBookIcon />,
        link: "/dashboard/store/grainreports",
        level: 1,
        items: [
          { title: "Ticket Entry", icon: null, link: "/dashboard/tickets", roles: "Create|Update|Delete Tickets", level: 2 },
          { title: "Ticket Approval", icon: null, link: "/dashboard/ticketapproval", roles: "Create|Update|Delete Tickets", level: 2 },
          { title: "Rates", icon: null, link: "/dashboard/rates", roles: "Create|Update|Delete Rates", level: 2 },
          { title: "Invoicing", icon: null, link: "/dashboard/invoicing", roles: "Create|Update|Delete Invoices", level: 2 },
          { title: "Estimates", icon: null, link: "/dashboard/estimates", roles: "Create|Update|Delete Estimates", level: 2 },
        ],
      },
    ],
    [unreadBadge]
  );

  // Check permissions
  const checkHasAnySubPerms = useCallback(
    (item) => {
      if (!item.items || item.items.length === 0) return true;
      return item.items.some(
        (subItem) =>
          subItem.title !== "DIVIDER" &&
          (_.isUndefined(subItem.roles) || roles.includes(subItem.roles))
      );
    },
    [roles]
  );

  // Single source of truth for whether a menu item renders: it must not be a
  // divider, the user must have the item's own role, and it must have at least
  // one permitted sub-item. Used by both renderMenuItem and sectionHasVisibleItems.
  const isItemVisible = useCallback(
    (item) => {
      if (item.title === "DIVIDER") {
        return false;
      }
      const hasPermission = _.isUndefined(item.roles) || roles.includes(item.roles);
      return hasPermission && checkHasAnySubPerms(item);
    },
    [roles, checkHasAnySubPerms]
  );

  // A section should only render if at least one of its items would render.
  const sectionHasVisibleItems = useCallback(
    (items) => items.some(isItemVisible),
    [isItemVisible]
  );

  const isActiveRoute = useCallback(
    (link) => {
      if (!link) return false;
      if (link === "/dashboard") return location.pathname === "/dashboard";
      return location.pathname.startsWith(link);
    },
    [location.pathname]
  );

  // Close all subdrawers
  const closeSubDrawers = useCallback(() => {
    setOpenSubDrawer(false);
    setOpenSubSubDrawer(false);
    setOpenSubSubSubDrawer(false);
    setSubDrawer(null);
    setSubSubDrawer(null);
    setSubSubSubDrawer(null);
  }, []);

  // Close everything (for click-away)
  const closeAllDrawers = useCallback(() => {
    if (!alwaysOpen) {
      setOpen(false);
    }
    closeSubDrawers();
  }, [alwaysOpen, closeSubDrawers]);

  // Handle clicking on an item (closes drawers after navigation)
  const handleItemClick = useCallback(() => {
    if (!alwaysOpen) {
      setOpen(false);
    }
    closeSubDrawers();
  }, [alwaysOpen, closeSubDrawers]);

  // Handle overlay click - cascade close deepest first, then drawer
  const handleOverlayClick = useCallback(() => {
    const delay = 75;
    let step = 0;

    if (openSubSubSubDrawer) {
      setTimeout(() => { setOpenSubSubSubDrawer(false); setSubSubSubDrawer(null); }, step * delay);
      step++;
    }
    if (openSubSubDrawer) {
      setTimeout(() => { setOpenSubSubDrawer(false); setSubSubDrawer(null); }, step * delay);
      step++;
    }
    if (openSubDrawer) {
      setTimeout(() => { setOpenSubDrawer(false); setSubDrawer(null); }, step * delay);
      step++;
    }
    if (!alwaysOpen) {
      setTimeout(() => { setOpen(false); }, step * delay);
    }
  }, [openSubSubSubDrawer, openSubSubDrawer, openSubDrawer, alwaysOpen]);

  const handleSubDrawerClick = useCallback(
    (component) => {
      if (_.isEqual(component, subDrawer)) {
        closeSubDrawers();
      } else {
        const needsDrawerOpen = !alwaysOpen && !open;
        setOpen(true);
        setSubDrawer(component);
        setSubSubDrawer(null);
        setSubSubSubDrawer(null);
        setOpenSubSubDrawer(false);
        setOpenSubSubSubDrawer(false);
        if (needsDrawerOpen) {
          setTimeout(() => setOpenSubDrawer(true), 200);
        } else {
          setOpenSubDrawer(true);
        }
      }
    },
    [subDrawer, closeSubDrawers, alwaysOpen, open]
  );

  const handleSubSubDrawerClick = useCallback(
    (component) => {
      if (_.isEqual(component, subSubDrawer)) {
        setOpenSubSubDrawer(false);
        setSubSubDrawer(null);
        setOpenSubSubSubDrawer(false);
        setSubSubSubDrawer(null);
      } else {
        setOpenSubSubDrawer(true);
        setSubSubDrawer(component);
      }
    },
    [subSubDrawer]
  );

  const handleSubSubSubDrawerClick = useCallback(
    (component) => {
      if (_.isEqual(component, subSubSubDrawer)) {
        setOpenSubSubSubDrawer(false);
        setSubSubSubDrawer(null);
      } else {
        setOpenSubSubSubDrawer(true);
        setSubSubSubDrawer(component);
      }
    },
    [subSubSubDrawer]
  );

  const handleExpandClick = useCallback(
    (section) => {
      const newValue = !expandedSections[section];
      setExpandedSections((prev) => ({ ...prev, [section]: newValue }));
      updateUserSettings(`${section}Expanded`, newValue);
    },
    [expandedSections, updateUserSettings]
  );

  const handleColorModeToggle = useCallback(() => {
    const newMode = props.colorMode === "light" ? "dark" : "light";
    props.setColorMode(newMode);
    updateUserSettings("colorMode", newMode);
  }, [props, updateUserSettings]);

  const handleSidebarToggle = useCallback(() => {
    const newValue = !alwaysOpen;
    setAlwaysOpen(newValue);
    updateUserSettings("sidebarAlwaysOpen", newValue);
    if (!newValue) {
      setOpen(false);
      closeSubDrawers();
    }
  }, [alwaysOpen, updateUserSettings, closeSubDrawers]);

  const handleLogout = useCallback(() => {
    auth.signOut().then(() => props.history.push("/dashboard"));
  }, [props.history]);

  const canSeeAdmin = useMemo(
    () => TIMESHEET_TYPES.some((t) => roles.includes(t.role)),
    [roles]
  );

  const canSeeShop = useMemo(
    () => sectionHasVisibleItems(SHOP_ITEMS),
    [sectionHasVisibleItems, SHOP_ITEMS]
  );

  // Render menu item with collapsed state support
  const renderMenuItem = useCallback(
    (item, index) => {
      if (item.title === "DIVIDER") {
        return <Divider key={`divider-${index}`} className={!open ? classes.dividerCollapsed : undefined} />;
      }

      if (!isItemVisible(item)) {
        return null;
      }

      const isActive = isActiveRoute(item.link);
      const hasSubItems = item.items && item.items.length > 0;

      const listItemContent = (
        <ListItem
          key={item.title}
          onClick={() =>
            hasSubItems
              ? handleSubDrawerClick(
                  item.title === "Human Resources"
                    ? <HRManagementDrawer />
                    : <SubDrawer roles={roles} level={item.level} items={item.items} />
                )
              : handleItemClick()
          }
          className={clsx(
            classes.listItem,
            { [classes.listItemActive]: isActive },
            { [classes.listItemCollapsed]: !open }
          )}
          component={!hasSubItems ? Link : "div"}
          to={!hasSubItems ? item.link : undefined}
          button
        >
          <ListItemIcon
            className={clsx(classes.listItemIcon, { [classes.listItemIconCollapsed]: !open })}
          >
            {item.icon}
          </ListItemIcon>
          {open && <ListItemText primary={item.title} />}
        </ListItem>
      );

      // Wrap in tooltip when collapsed
      if (!open) {
        return (
          <Tooltip key={item.title} title={item.title} placement="right" arrow>
            {listItemContent}
          </Tooltip>
        );
      }

      return listItemContent;
    },
    [roles, isItemVisible, isActiveRoute, handleSubDrawerClick, handleItemClick, open, classes]
  );

  // Render section with collapsed state support
  const renderSection = useCallback(
    (title, items, sectionKey) => (
      <List
        subheader={
          open ? (
            <ListSubheader
              className={classes.sectionHeader}
              onClick={() => handleExpandClick(sectionKey)}
            >
              {title}
              <IconButton
                className={clsx(classes.expand, {
                  [classes.expandOpen]: expandedSections[sectionKey],
                })}
                size="small"
              >
                <ExpandMoreIcon />
              </IconButton>
            </ListSubheader>
          ) : (
            <Tooltip title={`${expandedSections[sectionKey] ? "Collapse" : "Expand"} ${title}`} placement="right" arrow>
              <ListSubheader
                className={classes.sectionHeaderCollapsed}
                onClick={() => handleExpandClick(sectionKey)}
              >
                <IconButton
                  className={clsx(classes.expand, {
                    [classes.expandOpen]: expandedSections[sectionKey],
                  })}
                  size="small"
                  style={{ padding: 6 }}
                >
                  <ExpandMoreIcon style={{ fontSize: 20 }} />
                </IconButton>
              </ListSubheader>
            </Tooltip>
          )
        }
      >
        <Collapse in={expandedSections[sectionKey]} timeout="auto">
          {items.map((item, index) => renderMenuItem(item, index))}
        </Collapse>
      </List>
    ),
    [classes, expandedSections, handleExpandClick, renderMenuItem, open]
  );

  return (
    <>
      <BugReportModal open={bugReportOpen} onClose={() => setBugReportOpen(false)} />

      {/* Logout Confirmation Modal */}
      <Modal
        className={classes.modal}
        open={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={showLogoutDialog}>
          <Paper className={classes.modalPaper} style={{ textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              Log Out
            </Typography>
            <Typography color="textSecondary" style={{ marginBottom: 24 }}>
              Are you sure you want to log out?
            </Typography>
            <Grid container justifyContent="center" spacing={2}>
              <Grid item>
                <Button variant="outlined" onClick={() => setShowLogoutDialog(false)}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ExitToAppIcon />}
                  onClick={handleLogout}
                >
                  Log Out
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Fade>
      </Modal>

      <CssBaseline />

      {/* Overlay for click-away */}
      <div
        className={clsx(classes.overlay, { [classes.overlayHidden]: !showOverlay })}
        onClick={handleOverlayClick}
      />

      {/* App Bar */}
      <AppToolbar
        open={open}
        setOpen={setOpen}
        alwaysOpen={alwaysOpen}
        onSidebarToggle={handleSidebarToggle}
        colorMode={props.colorMode}
        onColorModeToggle={handleColorModeToggle}
        onBugReport={() => setBugReportOpen(true)}
        closeSubDrawers={closeSubDrawers}
        userInfo={userInfo}
      />

      {/* Main Container */}
      <div className={classes.container}>
        {/* Main Drawer */}
        <Drawer
          variant={smallScreen ? "temporary" : "permanent"}
          open={smallScreen ? open : undefined}
          onClose={smallScreen ? () => { setOpen(false); closeSubDrawers(); } : undefined}
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: open && alwaysOpen,
            [classes.drawerClose]: !open || !alwaysOpen,
          })}
          classes={{
            paper: clsx(classes.drawerPaper, {
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            }),
          }}
          style={{ zIndex: 1000 }}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
        >
          {/* Scrollable menu area */}
          <div className={open ? classes.drawerScrollArea : classes.drawerScrollAreaCollapsed}>
            {/* Admin Section */}
            {canSeeAdmin && (
              <>
                {renderSection("Admin", ADMIN_ITEMS, "admin")}
                <Divider className={!open ? classes.dividerCollapsed : undefined} />
              </>
            )}

            {/* General Section */}
            {renderSection("General", GENERAL_ITEMS, "general")}
            <Divider className={!open ? classes.dividerCollapsed : undefined} />

            {/* Shop Section */}
            {canSeeShop && (
              <>
                {renderSection("Online Store", SHOP_ITEMS, "shop")}
                <Divider className={!open ? classes.dividerCollapsed : undefined} />
              </>
            )}

            {/* Tech (dev only) */}
            {(currentUser.email === "derekmilleryoung@gmail.com" ||
              currentUser.email === "landryjrdn@gmail.com") && (
              <List>
                {!open ? (
                  <Tooltip title="Tech" placement="right" arrow>
                    <ListItem
                      onClick={handleItemClick}
                      className={clsx(classes.listItem, classes.listItemCollapsed, {
                        [classes.listItemActive]: isActiveRoute("/dashboard/Tech"),
                      })}
                      component={Link}
                      to="/dashboard/Tech"
                      button
                    >
                      <ListItemIcon className={clsx(classes.listItemIcon, classes.listItemIconCollapsed)}>
                        <ImportantDevicesIcon />
                      </ListItemIcon>
                    </ListItem>
                  </Tooltip>
                ) : (
                  <ListItem
                    onClick={handleItemClick}
                    className={clsx(classes.listItem, {
                      [classes.listItemActive]: isActiveRoute("/dashboard/Tech"),
                    })}
                    component={Link}
                    to="/dashboard/Tech"
                    button
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <ImportantDevicesIcon />
                    </ListItemIcon>
                    <ListItemText primary="Tech" />
                  </ListItem>
                )}
              </List>
            )}
          </div>

          {/* Bottom Section - My Page & Logout (pinned) */}
          <div className={classes.bottomSection}>
            <List>
              {!open ? (
                <>
                  <Tooltip title="My Page" placement="right" arrow>
                    <ListItem
                      onClick={handleItemClick}
                      className={clsx(classes.listItem, classes.listItemCollapsed, {
                        [classes.listItemActive]: isActiveRoute("/dashboard/users/me"),
                      })}
                      component={Link}
                      to="/dashboard/users/me"
                      button
                    >
                      <ListItemIcon className={clsx(classes.listItemIcon, classes.listItemIconCollapsed)}>
                        <Badge badgeContent={myTasksBadge} color="error" overlap="rectangular">
                          <AccountCircleIcon />
                        </Badge>
                      </ListItemIcon>
                    </ListItem>
                  </Tooltip>
                  <Tooltip title="Log Out" placement="right" arrow>
                    <ListItem
                      className={clsx(classes.listItem, classes.listItemCollapsed)}
                      onClick={() => setShowLogoutDialog(true)}
                      button
                    >
                      <ListItemIcon className={clsx(classes.listItemIcon, classes.listItemIconCollapsed)}>
                        <ExitToAppIcon />
                      </ListItemIcon>
                    </ListItem>
                  </Tooltip>
                </>
              ) : (
                <>
                  <ListItem
                    onClick={handleItemClick}
                    className={clsx(classes.listItem, {
                      [classes.listItemActive]: isActiveRoute("/dashboard/users/me"),
                    })}
                    component={Link}
                    to="/dashboard/users/me"
                    button
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <Badge badgeContent={myTasksBadge} color="error" overlap="rectangular">
                        <AccountCircleIcon />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText primary="My Page" />
                  </ListItem>
                  <ListItem
                    className={classes.listItem}
                    onClick={() => setShowLogoutDialog(true)}
                    button
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <ExitToAppIcon />
                    </ListItemIcon>
                    <ListItemText primary="Log Out" />
                  </ListItem>
                </>
              )}
            </List>
          </div>
        </Drawer>

        {/* Sub Drawers */}
        <div className={classes.subDrawersContainer}>
          <Paper className={openSubDrawer ? classes.subDrawerOpen : classes.subDrawerClose}>
            {smallScreen && openSubDrawer && (
              <div className={classes.subDrawerMobileHeader}>
                <IconButton onClick={closeSubDrawers} size="small">
                  <ArrowBackIcon />
                </IconButton>
                <Typography className={classes.subDrawerMobileTitle}>Menu</Typography>
              </div>
            )}
            {subDrawer &&
              React.cloneElement(subDrawer, {
                onClick: handleItemClick,
                openSubSubDrawer: handleSubSubDrawerClick,
              })}
          </Paper>

          <Paper className={openSubSubDrawer ? classes.subSubDrawerOpen : classes.subSubDrawerClose}>
            {smallScreen && openSubSubDrawer && (
              <div className={classes.subDrawerMobileHeader}>
                <IconButton onClick={() => { setOpenSubSubDrawer(false); setSubSubDrawer(null); setOpenSubSubSubDrawer(false); setSubSubSubDrawer(null); }} size="small">
                  <ArrowBackIcon />
                </IconButton>
                <Typography className={classes.subDrawerMobileTitle}>Back</Typography>
              </div>
            )}
            {subSubDrawer &&
              React.cloneElement(subSubDrawer, {
                onClick: handleItemClick,
                openSubSubDrawer: handleSubSubSubDrawerClick,
              })}
          </Paper>

          <Paper className={openSubSubSubDrawer ? classes.subSubSubDrawerOpen : classes.subSubSubDrawerClose}>
            {smallScreen && openSubSubSubDrawer && (
              <div className={classes.subDrawerMobileHeader}>
                <IconButton onClick={() => { setOpenSubSubSubDrawer(false); setSubSubSubDrawer(null); }} size="small">
                  <ArrowBackIcon />
                </IconButton>
                <Typography className={classes.subDrawerMobileTitle}>Back</Typography>
              </div>
            )}
            {subSubSubDrawer &&
              React.cloneElement(subSubSubDrawer, { onClick: handleItemClick })}
          </Paper>
        </div>

        {/* Main Content */}
        <main className={classes.content}>{props.children}</main>
      </div>
    </>
  );
};

export default withRouter(SideDrawer);
